import React, { useContext, useState, useMemo, useEffect, useRef } from 'react';
import styles from './styles/ClassroomCanvasOptions.module.css';
import CanvasContext from '../../../context/CanvasContext';
import { FaChevronRight, FaChevronDown, FaHistory, FaFileSignature, FaMapMarkerAlt, FaCheckSquare, FaKeyboard } from 'react-icons/fa';

const ClassroomCanvasOptions = ({ userInfo }) => {
    /**
     * 1.  Added a new state variable, `expandedItems`, which keeps track of whcih submissions are currently expanded
     *
     * 2.  New constant `allowMultiExpand` added:
     *     - If `false` (default), only one submission can be expanded at a time (single-expansion mode).
     *     - If you change this to `true`, multiple submissions can be expanded simultaneously (multi-expansion mode).
     *     This is in case I change my mind about how I want expansion to be handled.
     *
     * 3.  `handleSubmissionClick` function now now also handles the expansion/collapse.
     *
     * 4.  When a submission is expanded, the list of decisions is now sorted chronologically.
     *
     * 5.  To avoid mutating the original data, I'm using `.slice()` to
     *     create a shallow copy of the `decisions` array before sorting it for display. I probably forgot to do this in other places
     *     within the codebase...I'll probably do a sitewide code optimization sweep later.
     *
     * 6.  The key for each rendered decision now uses `decision._id`, which is a unique and stable. Should prevent any key warnings.
     */
    const {
        classroomStudentsWithLogs,
        assignmentWithSubmissions,
        highlightStudentLog,
        highlightSubmission,
        resetHighlightSubmission,
    } = useContext(CanvasContext);

    // --- CONFIGURATION FOR EXPANSION BEHAVIOR ---
    // Set to `true` to allow expanding multiple submissions at once.
    // Set to `false` for single-expansion mode.
    const allowMultiExpand = false;

    // State to track expanded submissions.
    // If multi-expand is on, we use a Set for efficient add/delete/has operations.
    // If it's off, we just store the single ID (string) or null.
    const [expandedItems, setExpandedItems] = useState(allowMultiExpand ? new Set() : null);
    const [currentSelectedStudent, setSelectedStudent] = useState(userInfo?.userId);

    const handleSubmissionClick = (submission) => {
        highlightSubmission(submission);

        if (allowMultiExpand) {
            setExpandedItems(prevIds => {
                const newIds = new Set(prevIds);
                if (newIds.has(submission._id)) {
                    newIds.delete(submission._id);
                } else {
                    newIds.add(submission._id);
                }
                return newIds;
            });
        } else {
            setExpandedItems(prevId =>
                prevId === submission._id ? null : submission._id
            );
        }
    };

    const isScenarioMode = !assignmentWithSubmissions;

    const dataSource = useMemo(() => {
        if (isScenarioMode) return classroomStudentsWithLogs;

        const userSubmissions = assignmentWithSubmissions?.submissions?.filter(
            sub => sub.viewer._id === userInfo?.userId
        );

        if (!userSubmissions?.length) return [];

        return userSubmissions.sort(
            (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
        );
    }, [assignmentWithSubmissions?.submissions, userInfo?.userId, isScenarioMode, classroomStudentsWithLogs]);

    const prevUserSubmissionsRef = useRef(null);

    useEffect(() => {
        if (!assignmentWithSubmissions?.submissions || !userInfo?.userId) return;
        if (userInfo?.userId !== currentSelectedStudent){
            resetHighlightSubmission();
            setSelectedStudent(userInfo?.userId);
        }

        const userSubmissions = assignmentWithSubmissions.submissions.filter((sub) => sub.viewer._id === userInfo.userId);
        const latestSubmissions = userSubmissions.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

        const serialized = JSON.stringify(latestSubmissions);

        if (prevUserSubmissionsRef.current !== serialized) {
            prevUserSubmissionsRef.current = serialized;

            
            if (latestSubmissions.length > 0) {
                //highlightSubmission(latestSubmissions[0]); 
            } else {
                resetHighlightSubmission();
            }
        }
    }, [assignmentWithSubmissions?.submissions, userInfo?.userId]);
    
    useEffect(() => {
        setExpandedItems(allowMultiExpand ? new Set() : null);
    }, [userInfo?.userId]);

    if (!dataSource?.length) {
        return (
            <div className={styles.container}>
                <p>No {isScenarioMode ? 'student logs' : 'submissions'} available.</p>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2>{isScenarioMode ? 'Classroom Logs' : 'Assignment Submissions'}</h2>
            </div>
            <div className={styles.optionsContainer}>
                {isScenarioMode ? (
                    dataSource.map((student) => (
                        <div key={student._id} className={styles.card}>
                            <div className={styles.cardHeader} style={{ borderLeftColor: student.color }}>
                                <h3>{student.username}</h3>
                            </div>
                            {student.logs.length === 0 ? (
                                <p className={styles.emptyStateText}>No logs for this scenario.</p>
                            ) : (
                                <ul className={styles.logList}>
                                    {student.logs.map((log) => (
                                        <li key={log._id}>
                                            <button
                                                onClick={() => highlightStudentLog(student._id, log)}
                                                className={styles.logItem}
                                            >
                                                <FaHistory className={styles.logIcon} />
                                                <span>Log on {new Date(log.createdAt).toLocaleString()}</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ))
                ) : (
                    dataSource.map((submission) => {
                        const isExpanded = allowMultiExpand
                            ? expandedItems.has(submission._id)
                            : expandedItems === submission._id;

                        return (
                            <div key={submission._id} className={styles.accordionItem}>
                                <button
                                    className={styles.accordionHeader}
                                    onClick={() => handleSubmissionClick(submission)}
                                >
                                    <div className={styles.headerInfo}>
                                        <span className={styles.colorDot} style={{ backgroundColor: submission.color }} />
                                        <div className={styles.headerText}>
                                            <h3>{submission.viewer.username}</h3>
                                            <span>{new Date(submission.updatedAt).toLocaleString()}</span>
                                        </div>
                                    </div>
                                    {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                                </button>
                                {isExpanded && (
                                    <div className={styles.accordionContent}>
                                        <ul className={styles.decisionList}>
                                            {submission.decisions.length > 0 ? (
                                                submission.decisions
                                                    .slice().filter(dec => dec.priority)
                                                    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                                                    .map((decision, index) => (
                                                        <li key={decision._id || index} className={styles.decisionItem}>
                                                            {decision.type === 'situation' && <><FaMapMarkerAlt className={styles.decisionIcon} /><span>Situation: {decision.title === "" ? 'untitled' : decision.title}</span></>}
                                                            {decision.type === 'choice' && <><FaCheckSquare className={styles.decisionIcon} /><span>Choice: {decision.title === "" ? 'untitled' : decision.title}</span></>}
                                                            {decision.type === 'input' && <><FaKeyboard className={styles.decisionIcon} /><span>Input: "{decision.userInput}"</span></>}
                                                        </li>
                                                    ))
                                            ) : (
                                                <li className={styles.emptyStateText}>No decisions recorded in this log.</li>
                                            )}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default ClassroomCanvasOptions;